import React, { ReactElement } from 'react';
import styled from 'styled-components';
import {
  Link,
  paletteHelper,
  spacingHelper,
  Text,
  typographyHelper,
} from '@rea-group/construct-kit-core';
import FlatmatesLogo from './FlatmatesLogo';
import RealestateLogo from './RealestateLogo';
import REAGroupLogo from './REAGroupLogo';
import RCALogo from './RCALogo';
import ProptrackLogo from './ProptrackLogo';
import MortgageChoiceLogo from './MortgageChoiceLogo';
import PropertyLogo from './PropertyLogo';
import SmartlineLogo from './SmartlineLogo';

const REASiteLinksContainer = styled.div`
  padding: 0 ${spacingHelper('medium threeExtraLarge')};
  background: ${paletteHelper('backgroundSecondaryBase')};
`;

const REASiteLinksStyle = styled.div`
  margin: 0 auto;
  max-width: ${({ theme }) => theme.maxWidth};
`;

const REAGroupSiteLinks = styled.div`
  padding-top: ${spacingHelper('small')};
  & > a {
    display: block;
    margin-top: ${spacingHelper('large')};
    margin-right: ${spacingHelper('small')};
    color: ${paletteHelper('textPrimary')};
  }
  & > a:focus {
    outline: ${({ theme }) =>
      `${theme.border.width.thin} solid ${theme.palette.borderFocused}`};
    outline-offset: 0.125rem;
  }
  ${({ theme }) => theme.layoutBreakpoints.medium} {
    & > a {
      display: inline-block;
    }
  }
  svg {
    display: block;
  }
`;

const FooterLink = styled(Link).attrs({ variant: 'secondary' })`
  ${typographyHelper('caption')}
  color: ${paletteHelper('backgroundPrimaryInverted')};
  text-decoration: none;
`;

const SiteLinks = styled.ul`
  li {
    display: inline-block;
    padding: 0 ${spacingHelper('extraSmall')};
    position: relative;
  }
  li:first-child {
    padding-left: 0;
  }
  & > li + li:after {
    content: '';
    height: 0.875rem;
    width: 1px;
    position: absolute;
    left: 0;
    top: 0.375rem;
    background-color: ${paletteHelper('textPrimary')};
  }
`;

const SitesTitle = styled(Text).attrs({ variant: 'caption' })`
  font-weight: bold;
`;

const SiteLinksContainer = styled.div`
  margin: ${spacingHelper('small')} 0;
`;

const StockInfo = styled.div`
  margin-top: ${spacingHelper('medium')};
`;

const REASiteLinks = (): ReactElement => (
  <REASiteLinksContainer>
    <REASiteLinksStyle>
      <REAGroupSiteLinks aria-label="REA Group links" role="navigation">
        <a href="https://www.rea-group.com" title="REA Group corporate website">
          <REAGroupLogo />
        </a>
        <a
          href="https://www.realestate.com.au"
          title="realestate.com.au: Real estate to buy and rent"
        >
          <RealestateLogo />
        </a>
        <a
          href="https://www.realcommercial.com.au"
          title="realcommercial.com.au: Search for commercial real estate"
        >
          <RCALogo />
        </a>
        <a
          href="https://www.proptrack.com.au"
          title="https://www.proptrack.com.au"
        >
          <ProptrackLogo />
        </a>
        <a
          href="https://flatmates.com.au/"
          title="Flatmates.com.au: Australia's biggest share accommodation site"
        >
          <FlatmatesLogo />
        </a>
        <a
          href="https://www.mortgagechoice.com.au/"
          title="https://www.mortgagechoice.com.au/"
        >
          <MortgageChoiceLogo />
        </a>
        <a
          href="https://www.smartline.com.au/"
          title="https://www.smartline.com.au/"
        >
          <SmartlineLogo />
        </a>
        <a
          href="https://www.property.com.au"
          title="https://www.property.com.au"
        >
          <PropertyLogo />
        </a>
      </REAGroupSiteLinks>

      <SiteLinksContainer aria-label="International sites" role="navigation">
        <SitesTitle>International sites</SitesTitle>
        <SiteLinks>
          <li>
            <FooterLink
              href="https://housing.com/"
              title="Property for sale and rent in India"
            >
              India
            </FooterLink>
          </li>
          <li>
            <FooterLink
              href="https://www.rumah.com/"
              title="Property for sale and rent in Indonesia"
            >
              Indonesia
            </FooterLink>
          </li>
          <li>
            <FooterLink
              href="https://www.iproperty.com.my/"
              title="Property for sale and rent in Malaysia"
            >
              Malaysia
            </FooterLink>
          </li>
          <li>
            <FooterLink
              href="https://www.propertyguru.com.sg/"
              title="Property for sale and rent in Singapore"
            >
              Singapore
            </FooterLink>
          </li>
          <li>
            <FooterLink
              href="https://www.ddproperty.com/"
              title="Property for sale and rent in Thailand"
            >
              Thailand
            </FooterLink>
          </li>
          <li>
            <FooterLink
              href="https://www.realtor.com/"
              title="Property for sale and rent in the United States"
            >
              United States
            </FooterLink>
          </li>
          <li>
            <FooterLink
              href="https://batdongsan.com.vn/"
              title="Property for sale and rent in Vietnam"
            >
              Vietnam
            </FooterLink>
          </li>
        </SiteLinks>
      </SiteLinksContainer>
      <SiteLinksContainer aria-label="Partner sites" role="navigation">
        <SitesTitle>Partner sites</SitesTitle>
        <SiteLinks>
          <li>
            <FooterLink
              href="https://news.com.au/"
              title="Latest news"
              target="_blank"
              rel="noopener noreferrer"
            >
              news.com.au
            </FooterLink>
          </li>
          <li>
            <FooterLink
              href="https://www.foxsports.com.au/"
              title="Sports news"
              target="_blank"
              rel="noopener noreferrer"
            >
              foxsports.com.au
            </FooterLink>
          </li>
          <li>
            <FooterLink
              href="https://www.homeguru.com.au/"
              title="Home valuations"
              target="_blank"
              rel="noopener noreferrer"
            >
              homeguru.com.au
            </FooterLink>
          </li>
          <li>
            <FooterLink
              href="https://www.mansionglobal.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Mansion Global
            </FooterLink>
          </li>
          <li>
            <FooterLink
              href="https://askizzy.org.au"
              title="Ask Izzy: The A to Z directory of homeless help"
              target="_blank"
              rel="noopener noreferrer"
            >
              askizzy.org.au
            </FooterLink>
          </li>
          <li>
            <FooterLink
              href="https://www.homeimprovementpages.com.au/"
              target="_blank"
              rel="noopener noreferrer"
            >
              hipages.com.au
            </FooterLink>
          </li>
          <li>
            <FooterLink
              href="https://www.makaan.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              makaan.com
            </FooterLink>
          </li>
          <li>
            <FooterLink
              href="https://www.proptiger.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              proptiger.com
            </FooterLink>
          </li>
          <li>
            <FooterLink
              href="https://thinkofliving.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              thinkofliving.com
            </FooterLink>
          </li>
        </SiteLinks>
      </SiteLinksContainer>
      <StockInfo>
        <Text variant="caption">
          realcommercial.com.au is owned and operated by ASX-listed REA Group
          Ltd (REA:ASX) &copy; REA Group Ltd.
        </Text>
        <Text variant="caption">
          {'By accessing or using our platform, you agree to our '}
          <FooterLink
            href="https://about.realestate.com.au/terms-use"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'underline' }}
          >
            Terms of Use.
          </FooterLink>
        </Text>
      </StockInfo>
    </REASiteLinksStyle>
  </REASiteLinksContainer>
);

export default REASiteLinks;
